import { supabase } from '@/integrations/supabase/client';
import { texToPlainText } from '@/utils/tex-parser';

export interface DraftMetadata {
  title: string;
  abstract: string;
  authors: string[];
}

/** Skeleton defaults that must never be published as real metadata. */
const PLACEHOLDER_TITLES = ['untitled draft', 'untitled', 'new draft'];
const PLACEHOLDER_ABSTRACT_HINT = 'this is a placeholder abstract';

export const isPlaceholderTitle = (value: string): boolean =>
  PLACEHOLDER_TITLES.includes(value.trim().toLowerCase());

export const isPlaceholderAbstract = (value: string): boolean =>
  value.trim().toLowerCase().startsWith(PLACEHOLDER_ABSTRACT_HINT);

/** Strip the abstract environment wrapper and LaTeX markup. */
export const cleanAbstractTex = (body: string): string => {
  const inner =
    body.match(/\\begin\{abstract\}([\s\S]*?)\\end\{abstract\}/)?.[1] ?? body;
  const withoutComments = inner
    .split('\n')
    .filter((line) => !line.trim().startsWith('%'))
    .join('\n');
  return texToPlainText(withoutComments).replace(/\s+/g, ' ').trim();
};

/** Split a LaTeX \author{...} value into individual names. */
export const splitTexAuthors = (value: string): string[] =>
  texToPlainText(value)
    .split(/\\and|\n|,| and /i)
    .map((name) => name.replace(/\s+/g, ' ').trim())
    .filter((name) => name.length > 1);

/**
 * Read the draft's own metadata (preamble title/authors + abstract section) so
 * the publish dialog can prefill. Everything is best-effort: an empty string
 * means "let the extraction job figure it out".
 */
export async function loadDraftMetadata(
  projectId: string,
  fallbackTitle?: string,
): Promise<DraftMetadata> {
  const result: DraftMetadata = { title: '', abstract: '', authors: [] };

  const [preambleRes, sectionRes] = await Promise.all([
    supabase
      .from('writer_project_preamble')
      .select('field, value')
      .eq('project_id', projectId),
    supabase
      .from('writer_project_sections')
      .select('body')
      .eq('project_id', projectId)
      .eq('anchor_id', 'abstract')
      .maybeSingle(),
  ]);

  const fields = new Map<string, string>();
  for (const row of (preambleRes.data ?? []) as { field: string; value: string }[]) {
    fields.set(row.field, row.value ?? '');
  }

  const rawTitle = texToPlainText(fields.get('title') ?? '').trim();
  const candidateTitle = rawTitle || (fallbackTitle ?? '').trim();
  if (candidateTitle && !isPlaceholderTitle(candidateTitle)) {
    result.title = candidateTitle;
  }

  const rawAbstract = cleanAbstractTex(
    ((sectionRes.data as { body?: string } | null)?.body ?? '') as string,
  );
  if (rawAbstract && !isPlaceholderAbstract(rawAbstract)) {
    result.abstract = rawAbstract;
  }

  result.authors = splitTexAuthors(fields.get('authors') ?? fields.get('author') ?? '');

  return result;
}
